import { createContext, useCallback, useContext, useEffect, useState, ReactNode, type Context } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { db } from '@/lib/supabaseQuery';
import { prepareOAuthRedirect, consumeOAuthLocale, markAsAuthenticated } from '@/utils/authRedirect';
import { logger } from '@/utils/logger';
import { SUPABASE_PROJECT_ID } from '@/config/supabase';
import {
  isRefreshTokenError,
  isTransientSessionError,
  SESSION_RETRY_DELAYS_MS,
  withSessionTimeout,
} from '@/lib/authSessionRecovery';

interface SignUpMetadata {
  locale?: string;
  country?: string;
  detected_location_id?: string;
}

interface OAuthResult {
  error: AuthError | null;
  url?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string, metadata?: SignUpMetadata) => Promise<{ error: AuthError | null; data: { user: { id: string; identities?: { id: string }[] } | null } | null }>;
  signInWithGoogle: () => Promise<OAuthResult>;
  signInWithLinkedIn: () => Promise<OAuthResult>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  resendConfirmationEmail: (email: string) => Promise<{ error: AuthError | null }>;
  loading: boolean;
  authInitialized: boolean;
  authUnavailable: boolean;
  retryAuthentication: () => void;
  passwordRecoveryPending: boolean;
  clearPasswordRecovery: () => void;
}

// Singleton context: during Vite HMR this module can be re-evaluated while the
// mounted AuthProvider still holds the previous context object. Reusing one
// instance across module re-evaluations prevents "useAuth must be used within
// AuthProvider" from being thrown after a hot update.
const globalScope = globalThis as typeof globalThis & {
  __taxNetworkAuthContext?: Context<AuthContextType | undefined>;
};

const AuthContext =
  globalScope.__taxNetworkAuthContext ??
  (globalScope.__taxNetworkAuthContext = createContext<AuthContextType | undefined>(undefined));

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // All hooks are unconditional at top level - crash-proof pattern
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [authAttempt, setAuthAttempt] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [passwordRecoveryPending, setPasswordRecoveryPending] = useState(false);

  const clearPasswordRecovery = () => setPasswordRecoveryPending(false);
  const retryAuthentication = useCallback((): void => {
    setError(null);
    setAuthUnavailable(false);
    setAuthInitialized(false);
    setLoading(true);
    setAuthAttempt(attempt => attempt + 1);
  }, []);

  useEffect(() => {
    let mounted = true;
    let initialSessionChecked = false;

    try {
      // Set up auth state listener FIRST (correct order per Supabase best practices)
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          if (!mounted) return;
          
          setSession(session);
          setUser(session?.user ?? null);
          
          // Only set loading to false after initial session check completes
          if (initialSessionChecked) {
            setLoading(false);
          }
          
          // Handle password recovery event — set flag to auto-open change dialog
          if (event === 'PASSWORD_RECOVERY' && session?.user) {
            markAsAuthenticated();
            setPasswordRecoveryPending(true);
            logger.info('[Auth] Password recovery session detected');
          }
          
          // Track login events and mark as returning visitor
          if (event === 'SIGNED_IN' && session?.user) {
            // Mark this browser as having authenticated (for smart auth mode detection)
            markAsAuthenticated();
            
            const provider = session.user.app_metadata?.provider;
            
            // Check for pending guest session to claim (stored before OAuth redirect)
            const pendingSessionId = sessionStorage.getItem('pendingGuestSessionId');
            if (pendingSessionId) {
              sessionStorage.removeItem('pendingGuestSessionId');
              // Claim the guest session in background
              supabase
                .from('ai_chat_sessions')
                .update({ user_id: session.user.id })
                .eq('id', pendingSessionId)
                .is('user_id', null)
                .then(({ error }) => {
                  if (error) {
                    logger.warn('[Auth] Failed to claim guest session', { error, sessionId: pendingSessionId });
                  } else {
                    logger.info('[Auth] Successfully claimed guest session', { sessionId: pendingSessionId });
                  }
                });
            }
            
            // Also check URL for session ID (for direct navigation to /chat/:sessionId then login)
            const urlMatch = window.location.pathname.match(/\/chat\/([a-f0-9-]{36})/i);
            if (urlMatch && urlMatch[1] && !pendingSessionId) {
              const urlSessionId = urlMatch[1];
              supabase
                .from('ai_chat_sessions')
                .update({ user_id: session.user.id })
                .eq('id', urlSessionId)
                .is('user_id', null)
                .then(({ error }) => {
                  if (!error) {
                    logger.info('[Auth] Claimed guest session from URL', { sessionId: urlSessionId });
                  }
                });
            }
            
            if (provider && provider !== 'email') {
              // OAuth login - track it and apply stored locale
              setTimeout(async () => {
                // Apply stored locale from before OAuth redirect
                const storedLocale = consumeOAuthLocale();
                if (storedLocale) {
                  const [, country] = storedLocale.split('-');
                  try {
                    await supabase.auth.updateUser({
                      data: { 
                        detected_locale: storedLocale, 
                        detected_country: country || null 
                      }
                    });
                    // Sync country to public.users (only if currently NULL)
                    if (country) {
                      db.update('users', { country }, [
                        { column: 'id', operator: 'eq', value: session.user.id },
                        { column: 'country', operator: 'is', value: null }
                      ], { returningColumns: 'id' }).catch(() => { /* Silent fail */ });
                    }
                  } catch {
                    // Silent fail for metadata update
                  }
                }
                
                // Track login event
                supabase.functions.invoke('track-analytics-event', {
                  body: {
                    actorId: session.user.id,
                    eventType: 'login',
                    eventCategory: 'auth',
                    sourcePage: '/auth',
                    metadata: { method: provider }
                  }
                }).catch(() => { /* Silent fail for tracking */ });
              }, 0);
            }
          }
        }
      );

      // THEN check for existing session.
      // With many tabs open, the cross-tab auth lock can be busy and getSession
      // fails with an acquire-timeout. That is "unknown", NOT "signed out" — so
      // retry with backoff instead of falling through to a guest state (which
      // downstream guards would turn into a real logout).
      const loadInitialSession = async (attempt = 0): Promise<void> => {
        if (!mounted) return;

        try {
          const { data, error: sessionError } = await withSessionTimeout(supabase.auth.getSession());
          if (!mounted) return;

          if (sessionError) {
            if (isTransientSessionError(sessionError) && attempt < SESSION_RETRY_DELAYS_MS.length) {
              logger.info('[Auth] Session check busy (cross-tab lock), retrying', {
                attempt: attempt + 1,
              });
              setTimeout(() => { void loadInitialSession(attempt + 1); }, SESSION_RETRY_DELAYS_MS[attempt]);
              return;
            }

            if (isTransientSessionError(sessionError)) {
              setError(sessionError);
              setAuthUnavailable(true);
              initialSessionChecked = true;
              setLoading(false);
              setAuthInitialized(true);
              return;
            }

            logger.warn('[Auth] Session error (non-fatal)', { error: sessionError });
            setError(sessionError);

            // Only a genuinely revoked/invalid refresh token justifies clearing
            // the stored session — never a lock timeout or a network blip.
            if (isRefreshTokenError(sessionError.message ?? '')) {
              logger.info('[Auth] Stale refresh token detected, clearing localStorage to prevent deadlock');
              try {
                localStorage.removeItem(`sb-${SUPABASE_PROJECT_ID}-auth-token`);
              } catch {
                // localStorage may be unavailable
              }
            }
          }

          setAuthUnavailable(false);
          initialSessionChecked = true;
          setSession(data.session);
          setUser(data.session?.user ?? null);
          setLoading(false);
          setAuthInitialized(true);
        } catch (err) {
          if (!mounted) return;

          if (isTransientSessionError(err) && attempt < SESSION_RETRY_DELAYS_MS.length) {
            logger.info('[Auth] Session check failed transiently, retrying', { attempt: attempt + 1 });
            setTimeout(() => { void loadInitialSession(attempt + 1); }, SESSION_RETRY_DELAYS_MS[attempt]);
            return;
          }

          logger.error('[Auth] Session remained unavailable after bounded retries', { error: err });
          setError(err instanceof Error ? err : new Error(String(err)));
          setAuthUnavailable(isTransientSessionError(err));
          initialSessionChecked = true;
          setLoading(false);
          setAuthInitialized(true);
        }
      };

      void loadInitialSession();

      // Cross-tab session sync: when another tab refreshes the token,
      // localStorage is updated. This listener re-syncs the session so
      // the current tab doesn't get signed out due to a revoked refresh token.
      const storageKey = `sb-${SUPABASE_PROJECT_ID}-auth-token`;
      const handleStorageChange = (event: StorageEvent) => {
        if (!mounted) return;
        if (event.key === storageKey) {
          if (event.newValue) {
            // Another tab refreshed the session — re-sync silently
            logger.info('[Auth] Cross-tab session update detected, re-syncing');
            supabase.auth.getSession().then(({ data: { session } }) => {
              if (!mounted) return;
              setSession(session);
              setUser(session?.user ?? null);
            });
          } else {
            // Another tab cleared the storage key — verify with the server
            // before signing out. A transient refresh-token error in one tab
            // should not cascade-logout every same-origin tab.
            logger.info('[Auth] Cross-tab storage clear detected, verifying session with server');
            supabase.auth.getSession().then(({ data: { session } }) => {
              if (!mounted) return;
              if (!session) {
                logger.info('[Auth] Server confirmed no session, signing out');
                setSession(null);
                setUser(null);
              } else {
                logger.info('[Auth] Cross-tab clear detected but our session is still valid, staying signed in');
              }
            });
          }
        }
      };
      window.addEventListener('storage', handleStorageChange);

      return () => {
        mounted = false;
        subscription.unsubscribe();
        window.removeEventListener('storage', handleStorageChange);
      };
    } catch (syncError) {
      // Catch any synchronous errors during subscription setup
      logger.error('[Auth] Subscription setup error', { error: syncError });
      setError(syncError instanceof Error ? syncError : new Error(String(syncError)));
      setLoading(false);
      setAuthInitialized(true);
    }
  }, [authAttempt]);

  const normalizeEmail = (email: string): string => email.trim().toLowerCase();

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = normalizeEmail(email);
    let { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    
    // WP password bridge: if login fails, try migrating from WordPress hash
    if (error && error.message?.includes('Invalid login credentials')) {
      try {
        const { data: bridgeData } = await supabase.functions.invoke('wp-password-bridge', {
          body: { email: normalizedEmail, password, mode: 'login' },
        });
        if (bridgeData?.migrated) {
          // Retry login — auth.users entry now exists with same password
          const retry = await supabase.auth.signInWithPassword({
            email: normalizedEmail,
            password,
          });
          data = retry.data;
          error = retry.error;
        }
      } catch {
        // Silent fail — proceed with original error
      }
    }
    
    if (data.user && !error) {
      // Track login via unified analytics
      try {
        await supabase.functions.invoke('track-analytics-event', {
          body: {
            actorId: data.user.id,
            eventType: 'login',
            eventCategory: 'auth',
            sourcePage: '/auth',
            metadata: { method: 'password' }
          }
        });
      } catch {
        // Silent fail for tracking
      }
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string, metadata?: SignUpMetadata) => {
    const normalizedEmail = normalizeEmail(email);
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('returnTo');
    // Use origin-only redirect to prevent race condition where /account guard
    // redirects to /auth before detectSessionInUrl processes the hash tokens.
    // The auth page and onAuthStateChange will handle post-login redirect.
    const redirectUrl = window.location.origin;

    const { error, data } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          detected_locale: metadata?.locale || null,
          detected_country: metadata?.country || null,
          detected_location_id: metadata?.detected_location_id || null,
        }
      }
    });
    return { error, data };
  };

  const signOut = async () => {
    setUser(null);
    setSession(null);
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const normalizedEmail = normalizeEmail(email);
    // Always redirect to /reset-password after password recovery.
    // Do NOT use returnTo here — it's meant for post-login navigation,
    // not for the password reset landing page.
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    // WP bridge: ensure auth.users entry exists before reset
    try {
      await supabase.functions.invoke('wp-password-bridge', {
        body: { email: normalizedEmail, mode: 'recovery' },
      });
    } catch {
      // Silent fail — proceed with reset regardless
    }
    
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const resendConfirmationEmail = async (email: string) => {
    const normalizedEmail = normalizeEmail(email);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: normalizedEmail,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error };
  };

  const signInWithGoogle = async (): Promise<OAuthResult> => {
    prepareOAuthRedirect();
    
    const redirectUrl = `${window.location.origin}/auth/callback?popup=true`;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        skipBrowserRedirect: true,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    return { error, url: data?.url ?? undefined };
  };

  const signInWithLinkedIn = async (): Promise<OAuthResult> => {
    prepareOAuthRedirect();
    
    const redirectUrl = `${window.location.origin}/auth/callback?popup=true`;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'linkedin_oidc',
      options: {
        redirectTo: redirectUrl,
        skipBrowserRedirect: true,
      }
    });
    return { error, url: data?.url ?? undefined };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      signIn, 
      signUp, 
      signInWithGoogle, 
      signInWithLinkedIn, 
      signOut, 
      resetPassword, 
      resendConfirmationEmail, 
      loading, 
      authInitialized,
      authUnavailable,
      retryAuthentication,
      passwordRecoveryPending,
      clearPasswordRecovery,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
